import useServiceData from './useServiceData';
import { useEffect, useMemo } from 'react';
import { useService } from '../redux/reducer/service';
import i18n, { defaultLanguage, languages, stores } from '../translations';
import { productsApi } from '../utils/api/productsApi';
import { getPlaidLanguageId } from '../translations/utils';
import { useCurrentStore } from './useCurrentStore';
import { useAvailableLanguages } from './useAvailableLanguages';
import { useApplicationCallbacks } from '../providers/application-callbacks';

const saveLanguageToCookie = (language) => {
  try {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `lang=${language}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  } catch (e) {
    console.warn('Не удалось сохранить язык в cookie:', e);
  }
};

const applyLanguage = (language, saveServiceDataFunc, callbacks) => {
  console.log('applyLanguage>>>>', language);

  i18n.changeLanguage(language);
  localStorage.setItem('lang', language);
  saveLanguageToCookie(language);
  saveServiceDataFunc('lang', language);
  changeHtmlAttrLang(language);

  productsApi.addHeaders({
    'plaid-language-id': getPlaidLanguageId(language),
  });

  if (callbacks) {
    refetchLanguageDependentData(callbacks);
  }
};

const changeHtmlAttrLang = (language) => {
  const html = document?.documentElement;
  if (!html) return;

  if (html.getAttribute('lang') !== language) {
    html.setAttribute('lang', language);
    html.setAttribute('data-lang', language);
  }
  html.setAttribute('dir', language === languages.ar ? 'rtl' : 'ltr');
};

const refetchLanguageDependentData = (callbacks) => {
  callbacks.getIndex();
  callbacks.getParams();
  callbacks.getFavoritesProducts();
  callbacks.getShopItems();
  callbacks.getQuests();
  callbacks.getIcons();
  callbacks.getModals();
};

export const useLanguageControls = () => {
  const { serviceData } = useService();
  const currentStore = useCurrentStore();
  const availableLanguages = useAvailableLanguages();
  const callbacks = useApplicationCallbacks();

  const { save } = useServiceData();

  const changeLanguage = (newLang) => {
    if (serviceData?.lang !== newLang) {
      applyLanguage(newLang, save, callbacks);
    }
  };

  const getLocalizedText = (value) => {
    if (typeof value === 'string') return value;

    // Берем текст строго по текущему языку пользователя.
    // Страновые (country) отличия уже учтены при сборке словарей i18n.
    const currentLang = serviceData?.lang || defaultLanguage; // "ru" | "kz" | "ar" | "en"

    return value[currentLang] || value.ru || Object.values(value)[0] || '';
  };

  const getLocalizedCurrency = () => {
    // TODO: нужен рефакторинг
    switch (currentStore) {
      case stores.ru:
        return '₽';
      case stores.kz:
        return '₸';
      case stores.ae:
        if (i18n.language === languages.ar) {
          return 'د.إ.';
        }
        return 'AED';
      case stores.qa:
        if (i18n.language === languages.ar) {
          return 'ر.ق';
        }
        return 'QAR';
      case stores.sa:
        if (i18n.language === languages.ar) {
          return 'ر.س';
        }
        return 'SAR';
      default:
        return '₽';
    }
  };

  // Вычисляем валидный язык для текущего магазина
  const validLanguage = useMemo(() => {
    const persistedLang = typeof window !== 'undefined' ? localStorage.getItem('lang') : null;
    let targetLang = serviceData?.lang || persistedLang;

    // Если язык не задан, используем defaultLanguage или первый доступный
    if (!targetLang) {
      targetLang = availableLanguages.includes(defaultLanguage)
        ? defaultLanguage
        : availableLanguages[0] || defaultLanguage;
    }

    // Проверяем, что выбранный язык доступен для текущего магазина
    // Если язык не доступен, используем defaultLanguage (если доступен) или первый доступный
    if (!availableLanguages.includes(targetLang)) {
      targetLang = availableLanguages.includes(defaultLanguage)
        ? defaultLanguage
        : availableLanguages[0] || defaultLanguage;
    }

    return targetLang;
  }, [serviceData?.lang, availableLanguages]);

  // Обновляем язык
  useEffect(() => {
    if (!validLanguage) return;

    if (i18n.language !== validLanguage) {
      console.log('useEffect [init]>>>>', validLanguage, i18n.language);

      const refetchCallbacks = validLanguage === defaultLanguage ? null : callbacks;
      applyLanguage(validLanguage, save, refetchCallbacks);
    } else {
      changeHtmlAttrLang(validLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validLanguage]);

  return {
    language: validLanguage,
    isArabic: validLanguage === languages.ar,
    changeLanguage,
    getLocalizedText,
    getLocalizedCurrency,
  };
};
